import React, { useCallback, useEffect, useState } from 'react';
import { useSearchParams } from 'react-router-dom';
import { Save, Trash2, Plus, CheckCircle2, AlertCircle, Calendar, LinkIcon, XCircle } from 'lucide-react';
import { api, ApiError } from '../../lib/api';
import DateInput from '../../components/ui/DateInput';

// /media/settings — Working hours, per-shooter buffer, and ad-hoc blocks.
//
// Google OAuth (connect/disconnect + target calendar pick) lands in MP.5.
// This slice (MP.2) leaves a placeholder so the section's shape is visible
// but the actual OAuth flow is deferred.

const DAYS = [
  { key: 'mon', label: 'Monday' },
  { key: 'tue', label: 'Tuesday' },
  { key: 'wed', label: 'Wednesday' },
  { key: 'thu', label: 'Thursday' },
  { key: 'fri', label: 'Friday' },
  { key: 'sat', label: 'Saturday' },
  { key: 'sun', label: 'Sunday' },
];

const HOUR_OPTIONS = Array.from({ length: 25 }, (_, i) => i);

export default function MediaSettings() {
  const [avail, setAvail] = useState(null);
  const [blocks, setBlocks] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState('');
  const [saving, setSaving] = useState(false);
  const [saved, setSaved] = useState(false);
  const [searchParams, setSearchParams] = useSearchParams();
  const googleFlash = searchParams.get('google');

  const load = useCallback(async () => {
    setLoading(true);
    setError('');
    try {
      const [a, b] = await Promise.all([
        api.get('/api/media/me/availability'),
        api.get('/api/media/me/blocks'),
      ]);
      setAvail(a);
      setBlocks(b?.blocks || []);
    } catch (err) {
      setError(err?.message || 'Failed to load settings');
    } finally {
      setLoading(false);
    }
  }, []);
  useEffect(() => { load(); }, [load]);

  const updateDay = (dayKey, value) => {
    setAvail((prev) => ({
      ...prev,
      workingHours: { ...prev.workingHours, [dayKey]: value },
    }));
  };

  const onSave = async () => {
    if (!avail || saving) return;
    setSaving(true);
    setError('');
    setSaved(false);
    try {
      const next = await api.put('/api/media/me/availability', {
        workingHours: avail.workingHours,
        bufferMinutes: avail.bufferMinutes,
      });
      setAvail(next);
      setSaved(true);
      setTimeout(() => setSaved(false), 2500);
    } catch (err) {
      setError(err?.message || 'Failed to save');
    } finally {
      setSaving(false);
    }
  };

  if (loading || !avail) {
    return <div className="text-[12px] text-gray-500">Loading…</div>;
  }

  return (
    <div className="max-w-3xl mx-auto">
      <header className="mb-6">
        <h1 className="text-2xl font-semibold text-white tracking-tight"><span className="serif-acc accent-text">Settings</span></h1>
        <p className="text-[13px] text-gray-500 mt-0.5">
          Working hours, buffer between events, and personal calendar blocks.
        </p>
      </header>

      {error && (
        <div className="mb-4 text-[12px] text-red-300 bg-red-500/10 border border-red-500/20 rounded-lg p-3 flex items-start gap-2">
          <AlertCircle className="w-3.5 h-3.5 mt-0.5 shrink-0" />
          {error}
        </div>
      )}

      <Section title="Working hours" description="Days and time windows the system can route bookings into. Schools will never see times outside these.">
        <div className="space-y-2">
          {DAYS.map(({ key, label }) => (
            <WorkingHourRow
              key={key}
              label={label}
              value={avail.workingHours[key]}
              onChange={(v) => updateDay(key, v)}
            />
          ))}
        </div>
      </Section>

      <Section title="Buffer between events" description="Minimum gap the system enforces between two of your bookings, in minutes.">
        <div className="flex items-center gap-3">
          <input
            type="number"
            min="0"
            max="240"
            step="15"
            value={avail.bufferMinutes}
            onChange={(e) => setAvail({ ...avail, bufferMinutes: Number(e.target.value) || 0 })}
            className="w-24 px-3 py-2 rounded-lg bg-white/[0.04] border border-white/[0.08] text-white text-sm focus:outline-none focus:border-[#B0FC38]/40"
          />
          <span className="text-[13px] text-gray-400">minutes</span>
        </div>
      </Section>

      <div className="flex items-center justify-end gap-3 mb-10">
        {saved && (
          <span className="text-[12px] text-[#B0FC38] inline-flex items-center gap-1">
            <CheckCircle2 className="w-3.5 h-3.5" /> Saved
          </span>
        )}
        <button
          type="button"
          onClick={onSave}
          disabled={saving}
          className="inline-flex items-center gap-2 px-4 py-2 rounded-full bg-[#B0FC38] text-gray-900 text-sm font-semibold hover:bg-[#9BE22E] disabled:opacity-60"
        >
          <Save className="w-4 h-4" />
          {saving ? 'Saving…' : 'Save changes'}
        </button>
      </div>

      <Section title="Calendar blocks" description="Mark personal unavailability: vacation, appointments, anything else. The system won't route bookings to you on these dates.">
        <BlocksManager blocks={blocks} onReload={load} />
      </Section>

      <GoogleCalendarSection
        availability={avail}
        googleFlash={googleFlash}
        clearFlash={() => {
          const sp = new URLSearchParams(searchParams);
          sp.delete('google');
          setSearchParams(sp, { replace: true });
        }}
        onChanged={() => load()}
        onPickCalendar={async (calendarId) => {
          const next = await api.put('/api/media/me/availability', {
            googleCalendarId: calendarId || null,
          });
          setAvail(next);
        }}
      />
    </div>
  );
}

function GoogleCalendarSection({ availability, googleFlash, clearFlash, onChanged, onPickCalendar }) {
  const [calendars, setCalendars] = useState([]);
  const [loadingCals, setLoadingCals] = useState(false);
  const [error, setError] = useState('');
  const status = availability.googleOauthStatus;
  const connected = status === 'connected';
  const expired = status === 'expired';

  useEffect(() => {
    if (!connected) return;
    setLoadingCals(true);
    api.get('/api/media/me/google/calendars').then((d) => {
      setCalendars(d?.calendars || []);
    }).catch(() => {}).finally(() => setLoadingCals(false));
  }, [connected]);

  const connect = async () => {
    try {
      const r = await api.get('/api/auth/google/start?return_to=/media/settings');
      window.location.href = r.authUrl;
    } catch (err) {
      setError(err instanceof ApiError ? err.message : 'Failed to start Google connect');
    }
  };

  const disconnect = async () => {
    if (!window.confirm('Disconnect Google? Auto-generated Meet links and calendar sync will stop until you reconnect.')) return;
    try {
      await api.post('/api/media/me/google/disconnect');
      onChanged?.();
    } catch (err) {
      setError(err instanceof ApiError ? err.message : 'Failed to disconnect');
    }
  };

  return (
    <Section title="Google Calendar"
      description="Connect your Google account to auto-generate Meet links for pre-event planning calls and to sync confirmed events to your calendar (one-way).">
      {googleFlash && (
        <FlashBanner flash={googleFlash} onDismiss={clearFlash} />
      )}
      {error && (
        <div className="mb-3 text-[12px] text-red-300 bg-red-500/10 border border-red-500/20 rounded-lg p-3">{error}</div>
      )}
      <div className="rounded-lg border border-white/[0.06] bg-white/[0.02] p-4 space-y-3">
        <div className="flex items-center justify-between gap-3 flex-wrap">
          <div className="flex items-center gap-2.5">
            <Calendar className="w-4 h-4 text-[#B0FC38]" />
            <div>
              <div className="text-[13px] text-white">
                {connected ? 'Connected' : expired ? 'Connection expired' : 'Not connected'}
              </div>
              <div className="text-[11px] text-gray-500">
                {connected
                  ? `Since ${availability.googleOauthConnectedAt ? new Date(availability.googleOauthConnectedAt).toLocaleDateString() : 'recently'}`
                  : expired
                    ? 'Reconnect to resume auto-generated Meet links.'
                    : 'Optional. Without this, Meet links are pasted manually.'}
              </div>
            </div>
          </div>
          {connected ? (
            <button type="button" onClick={disconnect}
              className="inline-flex items-center gap-1.5 px-3 py-1.5 rounded-full border border-white/[0.1] text-[12px] text-gray-300 hover:text-white hover:border-white/[0.2]">
              <XCircle className="w-3.5 h-3.5" /> Disconnect
            </button>
          ) : (
            <button type="button" onClick={connect}
              className="inline-flex items-center gap-1.5 px-4 py-1.5 rounded-full bg-[#B0FC38] text-gray-900 text-[12px] font-medium hover:bg-[#9BE22E]">
              <LinkIcon className="w-3.5 h-3.5" /> {expired ? 'Reconnect' : 'Connect Google'}
            </button>
          )}
        </div>
        {connected && (
          <div className="pt-3 border-t border-white/[0.05]">
            <label className="block">
              <span className="block text-[11px] uppercase tracking-wider text-gray-500 mb-1.5">Target calendar (for sync)</span>
              {loadingCals ? (
                <div className="text-[12px] text-gray-500">Loading calendars…</div>
              ) : (
                <select value={availability.googleCalendarId || ''}
                  onChange={(e) => onPickCalendar(e.target.value)}
                  className="w-full px-3 py-2 rounded-lg bg-white/[0.04] border border-white/[0.08] text-white text-[13px]">
                  <option value="">Primary calendar (default)</option>
                  {calendars.map((c) => (
                    <option key={c.id} value={c.id}>
                      {c.summary}{c.primary ? ' (primary)' : ''}
                    </option>
                  ))}
                </select>
              )}
            </label>
          </div>
        )}
      </div>
    </Section>
  );
}

const FLASH_COPY = {
  connected: { tone: 'success', text: 'Google account connected. Meet links will be auto-generated for new pre-event meetings.' },
  cancelled: { tone: 'info', text: 'Google connect cancelled.' },
  invalid: { tone: 'error', text: 'Google connect failed (invalid response). Try again.' },
  bad_state: { tone: 'error', text: 'Google connect failed (state mismatch). Try again.' },
  session_lost: { tone: 'error', text: 'Your session expired during Google connect. Sign in and try again.' },
  exchange_failed: { tone: 'error', text: 'Google rejected the authorization. Try again.' },
  no_refresh: { tone: 'error', text: 'Google did not return a refresh token. Try Reconnect.' },
  not_configured: { tone: 'error', text: 'Google OAuth is not configured on this environment.' },
};

function FlashBanner({ flash, onDismiss }) {
  const meta = FLASH_COPY[flash];
  if (!meta) return null;
  const cls = meta.tone === 'success'
    ? 'border-[#B0FC38]/30 bg-[#B0FC38]/[0.06] text-[#B0FC38]'
    : meta.tone === 'error'
    ? 'border-red-400/30 bg-red-500/[0.06] text-red-300'
    : 'border-white/[0.1] bg-white/[0.03] text-gray-300';
  return (
    <div className={`mb-3 text-[12px] rounded-lg border p-3 flex items-start justify-between gap-3 ${cls}`}>
      <span>{meta.text}</span>
      <button type="button" onClick={onDismiss} className="text-current opacity-60 hover:opacity-100">Dismiss</button>
    </div>
  );
}

function Section({ title, description, children }) {
  return (
    <section className="mb-8">
      <h2 className="text-[13px] font-medium text-white tracking-tight mb-1">{title}</h2>
      <p className="text-[12px] text-gray-500 mb-3">{description}</p>
      {children}
    </section>
  );
}

function WorkingHourRow({ label, value, onChange }) {
  const off = value === null;
  const [start, end] = off ? [9, 18] : value;
  return (
    <div className="flex items-center gap-3 px-3 py-2 rounded-lg border border-white/[0.05] bg-white/[0.02]">
      <div className="w-28 text-[13px] text-white">{label}</div>
      <label className="flex items-center gap-1.5 text-[12px] text-gray-400 cursor-pointer">
        <input
          type="checkbox"
          checked={!off}
          onChange={(e) => onChange(e.target.checked ? [start, end] : null)}
          className="w-3.5 h-3.5 rounded accent-[#B0FC38]"
        />
        Available
      </label>
      {!off && (
        <div className="flex items-center gap-2 ml-auto text-[12px] text-gray-300">
          <HourSelect value={start} onChange={(v) => onChange([v, Math.max(end, v + 1)])} />
          <span className="text-gray-500">to</span>
          <HourSelect value={end} onChange={(v) => onChange([Math.min(start, v - 1), v])} min={1} />
        </div>
      )}
    </div>
  );
}

function HourSelect({ value, onChange, min = 0 }) {
  return (
    <select
      value={value}
      onChange={(e) => onChange(Number(e.target.value))}
      className="px-2 py-1 rounded bg-white/[0.04] border border-white/[0.08] text-white text-[12px] focus:outline-none focus:border-[#B0FC38]/40"
    >
      {HOUR_OPTIONS.filter((h) => h >= min).map((h) => (
        <option key={h} value={h}>{formatHour(h)}</option>
      ))}
    </select>
  );
}

function formatHour(h) {
  if (h === 0 || h === 24) return '12 AM';
  if (h === 12) return '12 PM';
  return h < 12 ? `${h} AM` : `${h - 12} PM`;
}

function BlocksManager({ blocks, onReload }) {
  const [adding, setAdding] = useState(false);
  const [startAt, setStartAt] = useState('');
  const [endAt, setEndAt] = useState('');
  const [reason, setReason] = useState('');
  const [submitting, setSubmitting] = useState(false);
  const [err, setErr] = useState('');

  const onAdd = async (e) => {
    e.preventDefault();
    if (submitting) return;
    setSubmitting(true);
    setErr('');
    try {
      await api.post('/api/media/me/blocks', { startAt, endAt, reason: reason || null });
      setStartAt(''); setEndAt(''); setReason('');
      setAdding(false);
      onReload();
    } catch (e2) {
      setErr(e2?.message || 'Failed to add block');
    } finally {
      setSubmitting(false);
    }
  };

  const onDelete = async (id) => {
    try {
      await api.delete(`/api/media/me/blocks/${id}`);
      onReload();
    } catch (e2) {
      setErr(e2?.message || 'Failed to remove block');
    }
  };

  return (
    <div className="space-y-2">
      {blocks.map((b) => (
        <div key={b.id} className="flex items-center gap-3 px-3 py-2 rounded-lg border border-white/[0.05] bg-white/[0.02]">
          <div className="flex-1 min-w-0">
            <div className="text-[13px] text-white">
              {new Date(b.startAt).toLocaleString('en-US', { dateStyle: 'medium', timeStyle: 'short' })}
              {' → '}
              {new Date(b.endAt).toLocaleString('en-US', { dateStyle: 'medium', timeStyle: 'short' })}
            </div>
            {b.reason && <div className="text-[11px] text-gray-500 mt-0.5">{b.reason}</div>}
            {b.projectId && <div className="text-[10.5px] text-[#B0FC38]/80 mt-0.5" data-testid={`block-hold-${b.id}`}>Project shoot hold · moves with the project; Ops reschedules or cancels it</div>}
          </div>
          {!b.projectId && (
            <button
              type="button"
              onClick={() => onDelete(b.id)}
              className="p-1.5 text-gray-500 hover:text-red-400 hover:bg-red-500/[0.05] rounded transition-colors"
              title="Remove"
              aria-label="Remove block"
            >
              <Trash2 className="w-3.5 h-3.5" />
            </button>
          )}
        </div>
      ))}

      {!adding ? (
        <button
          type="button"
          onClick={() => setAdding(true)}
          className="inline-flex items-center gap-1.5 px-3 py-2 rounded-lg border border-white/[0.08] text-[12px] text-gray-300 hover:text-white hover:border-white/[0.15]"
        >
          <Plus className="w-3.5 h-3.5" /> Add a block
        </button>
      ) : (
        <form onSubmit={onAdd} className="rounded-lg border border-white/[0.06] bg-white/[0.02] p-3 space-y-2">
          {err && <div className="text-[11px] text-red-300">{err}</div>}
          <div className="grid grid-cols-1 md:grid-cols-2 gap-2">
            <label className="text-[11px] text-gray-500">
              Starts
              <div className="mt-1">
                <DateInput
                  withTime
                  value={startAt}
                  onChange={setStartAt}
                  required
                  className="block w-full px-3 py-2 rounded-lg bg-white/[0.04] border border-white/[0.08] text-white text-[13px] focus:outline-none focus:border-[#B0FC38]/40"
                />
              </div>
            </label>
            <label className="text-[11px] text-gray-500">
              Ends
              <div className="mt-1">
                <DateInput
                  withTime
                  value={endAt}
                  onChange={setEndAt}
                  required
                  className="block w-full px-3 py-2 rounded-lg bg-white/[0.04] border border-white/[0.08] text-white text-[13px] focus:outline-none focus:border-[#B0FC38]/40"
                />
              </div>
            </label>
          </div>
          <label className="text-[11px] text-gray-500 block">
            Reason <span className="text-gray-600">(optional, visible only to admin)</span>
            <input
              type="text"
              value={reason}
              onChange={(e) => setReason(e.target.value)}
              placeholder="e.g., Doctor's appointment"
              className="block w-full mt-1 px-3 py-2 rounded-lg bg-white/[0.04] border border-white/[0.08] text-white text-[13px] focus:outline-none focus:border-[#B0FC38]/40"
            />
          </label>
          <div className="flex items-center justify-end gap-2 pt-1">
            <button
              type="button"
              onClick={() => { setAdding(false); setErr(''); }}
              className="px-3 py-1.5 text-[12px] text-gray-400 hover:text-white"
            >
              Cancel
            </button>
            <button
              type="submit"
              disabled={submitting}
              className="px-3 py-1.5 rounded-lg bg-[#B0FC38] text-gray-900 text-[12px] font-medium hover:bg-[#9BE22E] disabled:opacity-60"
            >
              {submitting ? 'Adding…' : 'Add block'}
            </button>
          </div>
        </form>
      )}
    </div>
  );
}
